import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  ButtonGroup,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Footer from "../components/Footer";
import Navigation from "../components/Navigation";
import AdminPageHeader from "../components/AdminPageHeader";
import { useApi } from "../hooks/useApi";
import { useQuery } from "@tanstack/react-query";

function formatDate(isoString) {
  if (!isoString) return "TBD";
  try {
    return new Date(isoString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return isoString;
  }
}

function formatTime(isoString) {
  if (!isoString) return "TBD";
  try {
    return new Date(isoString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  } catch {
    return isoString;
  }
}

function isUpcoming(startsAt) {
  if (!startsAt) return false;
  return new Date(startsAt) >= new Date();
}

export default function CircleEvents() {
  const api = useApi();
  const navigate = useNavigate();
  const [page, setPage] = React.useState(1);
  const [sortOrder, setSortOrder] = React.useState("desc"); // default: newest first

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["circle-events", page, sortOrder],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("per_page", "50");
      params.set("sortOrder", sortOrder);
      return api.get(`/admin/circle/events?${params.toString()}`);
    },
  });

  const handleSortChange = (order) => {
    setSortOrder(order);
    setPage(1); // Reset to first page when sort changes
  };

  const events = data?.events ?? [];
  const pagination = {
    page: data?.page ?? page,
    per_page: data?.per_page ?? 50,
    count: data?.count ?? 0,
    has_next_page: data?.has_next_page ?? false,
    page_count: data?.page_count,
  };

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            backgroundColor: (theme) => theme.palette.background.default,
          }}
        >
          <Toolbar />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard"
                  sx={{
                    textDecoration: "none",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Home
                </Typography>
                <Typography
                  color="primary"
                  component={Link}
                  to="/dashboard/circle"
                  sx={{
                    textDecoration: "none",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Circle
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  Events
                </Typography>
              </Breadcrumbs>
            </Box>

            <AdminPageHeader
              title="Circle Events"
              description="View all Circle community events"
              icon={<EventIcon sx={{ color: "primary.main" }} />}
              children={
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                  <ButtonGroup size="small" variant="outlined">
                    <Button
                      onClick={() => handleSortChange("desc")}
                      variant={sortOrder === "desc" ? "contained" : "outlined"}
                    >
                      Newest first
                    </Button>
                    <Button
                      onClick={() => handleSortChange("asc")}
                      variant={sortOrder === "asc" ? "contained" : "outlined"}
                    >
                      Oldest first
                    </Button>
                  </ButtonGroup>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => refetch()}
                    disabled={isLoading}
                  >
                    Refresh
                  </Button>
                </Box>
              }
            />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.message}
              </Alert>
            )}

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No events found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        events.map((event) => (
                          <TableRow
                            key={event.id}
                            hover
                            onClick={() => navigate(`/dashboard/circle/events/${event.id}`)}
                            sx={{ cursor: "pointer" }}
                          >
                            <TableCell>
                              <Typography fontWeight={500}>
                                {event.name || "Untitled"}
                              </Typography>
                              {event.body && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    maxWidth: 300,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {event.body}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {formatDate(
                                event.starts_at || event.start_at || event.date
                              )}
                            </TableCell>
                            <TableCell>
                              {formatTime(
                                event.starts_at || event.start_at
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  isUpcoming(
                                    event.starts_at || event.start_at
                                  )
                                    ? "Upcoming"
                                    : "Past"
                                }
                                size="small"
                                color={
                                  isUpcoming(
                                    event.starts_at || event.start_at
                                  )
                                    ? "success"
                                    : "default"
                                }
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                {(pagination.count > 0 || events.length > 0) && (
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderTop: 1,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Page {pagination.page}
                      {pagination.page_count != null && ` of ${pagination.page_count}`} •{" "}
                      {events.length} on this page
                      {pagination.count > 0 && ` • ${pagination.count} total`}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Button
                        size="small"
                        disabled={pagination.page <= 1 || isLoading}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        startIcon={<ChevronLeftIcon />}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        disabled={!pagination.has_next_page || isLoading}
                        onClick={() => setPage((p) => p + 1)}
                        endIcon={<ChevronRightIcon />}
                      >
                        Next
                      </Button>
                    </Box>
                  </Box>
                )}
              </Paper>
            )}
          </Container>
          <Footer />
        </Box>
      </Box>
    </>
  );
}
